// blockerTaxonomy.js — taxonomia de BLOCKERS do fim de turno (padrão DeerFlow /goal).
// @arch:pure-engine
//
// ANTES: shouldAutoContinue era binário (2 stopReasons resumáveis; TODO o resto parava)
// e o terminalNotice cobria ~13 stops — os demais terminavam em SILÊNCIO (return null),
// a raiz do "o turno parou/congelou e não sei por quê" (classe #603).
//
// AGORA: TODO stopReason é classificado num tipo acionável (DeerFlow):
//   done              — terminou (silêncio ok)
//   cancelled         — o USUÁRIO parou (silêncio ok — ele sabe)
//   goal_not_met_yet  — incompleto mas retomável → AUTO-CONTINUE
//   needs_user_input  — precisa de decisão/ação humana → PERGUNTA, nunca silêncio
//   missing_evidence  — alegação sem verificador → cobrar evidência
//   external_wait     — esperando algo externo (CI/deploy/fila) — reservado
//   run_failed        — falha dura → notícia TIPADA, nunca silêncio
//
// PURO: sem imports de agentLoop (evita ciclo). Consumidores: agentAutoContinue
// (resumable) e terminalNotice (fallback tipado no lugar do return null).

export const BLOCKER = Object.freeze({
  DONE: 'done',
  CANCELLED: 'cancelled',
  CONTINUE: 'goal_not_met_yet',
  ASK_USER: 'needs_user_input',
  MISSING_EVIDENCE: 'missing_evidence',
  EXTERNAL_WAIT: 'external_wait',
  FAILED: 'run_failed',
});

// stopReason → tipo. Um stop DESCONHECIDO é FALHA (nunca silêncio) — se o loop
// ganhar um stop novo e ninguém classificar, o usuário ainda vê uma notícia tipada.
const MAP = Object.freeze({
  // terminou de verdade
  done: BLOCKER.DONE,
  plan_presented: BLOCKER.DONE,
  plan_prose: BLOCKER.DONE,
  conversational_reply: BLOCKER.DONE,
  // o usuário mandou parar
  aborted: BLOCKER.CANCELLED,
  // incompleto mas retomável (mais uma rodada pode fechar)
  max_iterations: BLOCKER.CONTINUE,
  tools_done_followup_failed: BLOCKER.CONTINUE,
  output_truncated: BLOCKER.CONTINUE, // WS-E: o nudge de chunking está na convo; a rodada dá a chance
  // precisa do humano
  no_root: BLOCKER.ASK_USER,
  no_chat_fn: BLOCKER.ASK_USER,
  autocorrect_cap: BLOCKER.ASK_USER,
  context_overflow: BLOCKER.ASK_USER,
  budget_exceeded: BLOCKER.ASK_USER,
  content_filtered: BLOCKER.ASK_USER,
  stall: BLOCKER.ASK_USER,
  stall_gave_up: BLOCKER.ASK_USER,
  tool_repeat_loop: BLOCKER.ASK_USER,
  tool_failure_spiral: BLOCKER.ASK_USER,
  blocked_permission: BLOCKER.ASK_USER,
  // alegou verde sem verificador rodar
  unbacked_green_claim: BLOCKER.MISSING_EVIDENCE,
  // alegou ter escrito arquivo sem nenhuma tool de escrita (após o nudge)
  unbacked_write_claim: BLOCKER.MISSING_EVIDENCE,
  // falha dura
  llm_timeout: BLOCKER.FAILED,
  model_error: BLOCKER.FAILED,
  exception: BLOCKER.FAILED,
  identical_output_loop: BLOCKER.FAILED,
  task_failed: BLOCKER.FAILED,
  cycle: BLOCKER.FAILED,
  invalid_graph: BLOCKER.FAILED,
});

/**
 * Classifica o fim de um run. @param {{ stopReason?: string, ok?: boolean,
 * autoCorrect?: { reachedGreen?: boolean }|null, blocked?: boolean, blockedReason?: string }} result
 * @returns {{ type: string, resumable: boolean }}
 */
export function classifyBlocker(result) {
  const stop = result?.stopReason || '';
  // DAG bloqueado carrega o motivo em blockedReason (dagPlanBridge) — classifica por ele.
  const key = result?.blocked && result?.blockedReason ? result.blockedReason : stop;
  let type;
  if (key === 'done_after_budget') {
    // Terminou POR orçamento: se o verificador rodou e ficou vermelho, a meta não foi
    // atingida (goal_not_met_yet, estilo DeerFlow) — mais uma rodada pode fechar.
    const ranRed = result?.autoCorrect && result.autoCorrect.reachedGreen === false;
    type = ranRed ? BLOCKER.CONTINUE : BLOCKER.DONE;
  } else {
    type = MAP[key] || (key ? BLOCKER.FAILED : BLOCKER.DONE);
  }
  return { type, resumable: type === BLOCKER.CONTINUE };
}

// Notícia genérica TIPADA para stops sem mensagem específica no terminalNotice —
// fecha o buraco do `return null` (stop de falha nunca mais termina em silêncio).
const FALLBACK_TEXT = Object.freeze({
  [BLOCKER.CONTINUE]: (stop) => `⏹️ O turno parou incompleto (${stop}) — diga "continue" para retomar de onde parou.`,
  [BLOCKER.ASK_USER]: (stop) => `⚠️ O agente precisa de você para seguir (${stop}). Ajuste o que foi pedido acima ou responda para destravar.`,
  [BLOCKER.MISSING_EVIDENCE]: (stop) => `⚠️ Conclusão sem evidência (${stop}) — rode a verificação de verdade antes de confiar no resultado.`,
  [BLOCKER.EXTERNAL_WAIT]: (stop) => `⏳ Aguardando um processo externo (${stop}) — o turno retoma quando ele concluir.`,
  [BLOCKER.FAILED]: (stop) => `⚠️ O turno falhou (${stop}). O estado está preservado — tente de novo ou reformule a tarefa.`,
});

/**
 * Fallback do terminalNotice: mensagem system TIPADA quando nenhum caso específico
 * casou. DONE/CANCELLED → null (silêncio correto). @returns {{role:'system',content:string}|null}
 */
export function blockerFallbackNotice(result) {
  const stop = result?.stopReason;
  if (!stop) return null;
  const { type } = classifyBlocker(result);
  if (type === BLOCKER.DONE || type === BLOCKER.CANCELLED) return null;
  const build = FALLBACK_TEXT[type] || FALLBACK_TEXT[BLOCKER.FAILED];
  return { role: 'system', content: build(stop), metadata: { kind: 'turn_blocker', blocker: type, stopReason: stop } };
}
